import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = { class: "w-full min-w-[980px] border-collapse text-left" }
const _hoisted_3 = {
  key: 0,
  class: "w-10 border-b border-lightGray30 px-2.5 py-2.5"
}
const _hoisted_4 = ["checked", "data-testid"]
const _hoisted_5 = ["aria-sort"]
const _hoisted_6 = ["data-testid", "onClick"]
const _hoisted_7 = { "aria-hidden": "true" }
const _hoisted_8 = {
  key: 1,
  class: "border-b border-lightGray30 px-2.5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-neutral50"
}
const _hoisted_9 = ["data-testid"]
const _hoisted_10 = {
  key: 0,
  class: "border-b border-lightGray30 px-2.5 py-2.5"
}
const _hoisted_11 = ["aria-label", "checked", "data-testid", "onChange"]
const _hoisted_12 = {
  key: 1,
  class: "border-b border-lightGray30 px-2.5 py-2.5"
}
const _hoisted_13 = { class: "flex flex-wrap gap-1" }

import { computed } from 'vue'

import type { QueueColumn, QueueSort } from './types'


export default /*#__PURE__*/_defineComponent({
  __name: 'QueueTable',
  props: {
    rows: {},
    columns: {},
    rowKey: {},
    selectable: { type: Boolean, default: false },
    selectedIds: { default: () => [] },
    isRowSelectable: { type: Function, default: undefined },
    sort: { default: null },
    testId: { default: 'queue-table' }
  },
  emits: ["update:selectedIds", "update:sort"],
  setup(__props: any, { emit }) {

const props = __props;





const canSelect = (row: T): boolean => props.isRowSelectable?.(row) ?? true

const selectableIds = computed(() => props.rows.filter(canSelect).map(props.rowKey))

const areAllSelected = computed(
  () =>
    selectableIds.value.length > 0 &&
    selectableIds.value.every((id) => props.selectedIds.includes(id))
)

function toggleOne(id: number, checked: boolean): void {
  const next = checked
    ? [...props.selectedIds, id]
    : props.selectedIds.filter((selected) => selected !== id)
  emit('update:selectedIds', next)
}

/**
 * "Todos" é todos DESTA página e selecionáveis. Ids já selecionados fora do
 * recorte visível continuam selecionados: desmarcar o cabeçalho não pode apagar
 * uma seleção que a pessoa fez noutra página.
 */
function toggleAll(checked: boolean): void {
  const visible = selectableIds.value
  const rest = props.selectedIds.filter((id) => !visible.includes(id))
  emit('update:selectedIds', checked ? [...rest, ...visible] : rest)
}

function toggleSort(key: string): void {
  const direction = props.sort?.key === key && props.sort.direction === 'asc' ? 'desc' : 'asc'
  emit('update:sort', { key, direction })
}

const sortIndicator = (key: string): string =>
  props.sort?.key !== key ? '' : props.sort.direction === 'asc' ? '▲' : '▼'

const ariaSortFor = (key: string): 'ascending' | 'descending' | 'none' =>
  props.sort?.key !== key ? 'none' : props.sort.direction === 'asc' ? 'ascending' : 'descending'

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "w-full overflow-x-auto rounded-lg border border-lightGray30 bg-white",
    "data-testid": _ctx.testId
  }, [
    _createElementVNode("table", _hoisted_2, [
      _createElementVNode("thead", null, [
        _createElementVNode("tr", null, [
          (_ctx.selectable)
            ? (_openBlock(), _createElementBlock("th", _hoisted_3, [
                _createElementVNode("input", {
                  type: "checkbox",
                  class: "h-4 w-4 cursor-pointer accent-primary",
                  "aria-label": "Selecionar todos",
                  checked: areAllSelected.value,
                  "data-testid": `${_ctx.testId}-select-all`,
                  onChange: _cache[0] || (_cache[0] = ($event: any) => (toggleAll(($event.target as HTMLInputElement).checked)))
                }, null, 40, _hoisted_4)
              ]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column) => {
            return (_openBlock(), _createElementBlock("th", {
              key: column.key,
              class: _normalizeClass(["border-b border-lightGray30 px-2.5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-neutral50", [column.widthClass, column.align === 'right' ? 'text-right' : 'text-left']]),
              "aria-sort": ariaSortFor(column.key)
            }, [
              (column.sortable)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "inline-flex items-center gap-1 uppercase tracking-wider",
                    "data-testid": `${_ctx.testId}-sort-${column.key}`,
                    onClick: ($event: any) => (toggleSort(column.key))
                  }, [
                    _createTextVNode(_toDisplayString(column.label) + " ", 1),
                    _createElementVNode("span", _hoisted_7, _toDisplayString(sortIndicator(column.key)), 1)
                  ], 8, _hoisted_6))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode(_toDisplayString(column.label), 1)
                  ], 64))
            ], 10, _hoisted_5))
          }), 128)),
          (_ctx.$slots.actions)
            ? (_openBlock(), _createElementBlock("th", _hoisted_8, " Ações "))
            : _createCommentVNode("", true)
        ])
      ]),
      _createElementVNode("tbody", null, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.rows, (row) => {
          return (_openBlock(), _createElementBlock("tr", {
            key: _ctx.rowKey(row),
            class: "align-top",
            "data-testid": `${_ctx.testId}-row-${_ctx.rowKey(row)}`
          }, [
            (_ctx.selectable)
              ? (_openBlock(), _createElementBlock("td", _hoisted_10, [
                  (canSelect(row))
                    ? (_openBlock(), _createElementBlock("input", {
                        key: 0,
                        type: "checkbox",
                        class: "h-4 w-4 cursor-pointer accent-primary",
                        "aria-label": `Selecionar item ${_ctx.rowKey(row)}`,
                        checked: _ctx.selectedIds.includes(_ctx.rowKey(row)),
                        "data-testid": `${_ctx.testId}-select-${_ctx.rowKey(row)}`,
                        onChange: ($event: any) => (toggleOne(_ctx.rowKey(row), ($event.target as HTMLInputElement).checked))
                      }, null, 40, _hoisted_11))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column) => {
              return (_openBlock(), _createElementBlock("td", {
                key: column.key,
                class: _normalizeClass(["border-b border-lightGray30 px-2.5 py-2.5 text-sm text-textPrimary", column.align === 'right' ? 'text-right' : 'text-left'])
              }, [
                _renderSlot(_ctx.$slots, `cell-${column.key}`, { row: row }, () => [
                  _createTextVNode(_toDisplayString(column.value?.(row) ?? ''), 1)
                ])
              ], 2))
            }), 128)),
            (_ctx.$slots.actions)
              ? (_openBlock(), _createElementBlock("td", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, [
                    _renderSlot(_ctx.$slots, "actions", { row: row })
                  ])
                ]))
              : _createCommentVNode("", true)
          ], 8, _hoisted_9))
        }), 128))
      ])
    ])
  ], 8, _hoisted_1))
}
}

})